import { Root, Thumb } from '@radix-ui/react-switch';
import { useId } from 'react';
import { twMerge } from 'tailwind-merge';

type SwitchProps = {
  onCheckedChange: (value: boolean) => unknown;
  checked: boolean;
  id?: string;
  size?: 'sm' | 'md';
  disabled?: boolean;
  isNew?: boolean;
};

export function Switch(props: SwitchProps) {
  const { onCheckedChange, checked, id, disabled = false, isNew = false } = props;
  const sizeClassName = {
    sm: 'w-8 h-4',
    md: 'w-10 h-5',
  }[props.size ?? 'md'];
  const thumbWidthClassName = {
    sm: 'size-3',
    md: 'size-4',
  }[props.size ?? 'md'];
  return (
    <Root
      className={twMerge(
        'relative cursor-pointer rounded-full bg-branding-gray shadow-sm outline-none hover:bg-branding-gray-100 focus:shadow-switchFocus focus:ring-4 focus:ring-branding-accent-200 data-[state=checked]:bg-branding-accent data-[state=checked]:hover:bg-branding-accent-700 disabled:bg-branding-gray-200',
        sizeClassName,
        isNew && 'data-[state=checked]:ring-4 data-[state=checked]:ring-branding-accent-200'
      )}
      onCheckedChange={onCheckedChange}
      checked={checked}
      id={id ?? undefined}
      disabled={disabled}
    >
      <Thumb
        className={twMerge(
          'block size-4 translate-x-0.5 rounded-full shadow-switchThumb transition-transform duration-100 will-change-transform',
          props.size === 'md'
            ? 'data-[state=checked]:translate-x-[1.3rem]'
            : 'data-[state=checked]:translate-x-[1.1rem]',
          thumbWidthClassName,
          disabled ? 'bg-branding-gray-50' : 'bg-white'
        )}
      />
    </Root>
  );
}

type SwitchFieldProps = SwitchProps & {
  label: string;
  labelClassName?: string;
  labelPosition?: 'left' | 'right';
};

export function SwitchField(props: SwitchFieldProps) {
  const { size = 'md', labelPosition = 'right', disabled = false, isNew = false } = props;
  const generatedId = useId();
  const switchId = props.id ?? generatedId;

  return (
    <div
      className={twMerge(
        'flex items-center gap-2',
        labelPosition === 'right' ? 'flex-row justify-start' : 'flex-row-reverse justify-end'
      )}
    >
      <Switch
        onCheckedChange={props.onCheckedChange}
        checked={props.checked}
        id={switchId}
        size={size}
        disabled={disabled}
        isNew={isNew}
      />
      <label
        className={twMerge(
          'font-body-2-r',
          disabled ? 'cursor-default' : 'cursor-pointer',
          props.labelClassName ?? ''
        )}
        htmlFor={switchId}
      >
        {props.label}
      </label>
    </div>
  );
}
